import React, { useState } from 'react';

import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { GridApi } from 'ag-grid-community';
import { RoyaltiesGridData } from 'src/hooks/analytics';

import { MetricsGrid } from '../../MetricsGrid';
import { royaltiesGridColDefs } from './royaltiesGridColDefs';

/**
 * Props for the RoyaltiesView component
 */
export interface RoyaltiesViewProps {
    /** Optional filter text for global filtering */
    filterText?: string;
    /** Array of royalties data to display */
    data?: RoyaltiesGridData[];
    /** Loading state (unused but kept for compatibility) */
    loading?: boolean;
    /** Error message (unused but kept for compatibility) */
    error?: string;
    /** Whether all data has been loaded */
    isDataFullyLoaded?: boolean;
    /** Loading completion percentage (0-100) */
    completionPercentage?: number;
}

/**
 * RoyaltiesView component displays royalties data in a grid format
 * with simplified filtering and loading progress indicators.
 */
export const RoyaltiesView: React.FC<RoyaltiesViewProps> = ({
    filterText = '',
    data = [],
    isDataFullyLoaded = true,
    completionPercentage = 100
}) => {
    const [agGridApi, setAgGridApi] = useState<GridApi>();

    const defaultSortModel = [
        { colId: 'month', sort: 'desc' as const },
        { colId: 'earningInUSD', sort: 'desc' as const }
    ];

    // Simple grid ready handler
    const handleGridReady = (api: GridApi) => {
        setAgGridApi(api);
    };

    // Show no data state
    if (isDataFullyLoaded && data.length === 0) {
        return (
            <Column heights="fill" alignmentHorizontal="center" alignmentVertical="center">
                <Text>No royalties data available</Text>
            </Column>
        );
    }

    return (
        <Column heights={['fit', 'fill']}>
            {!isDataFullyLoaded && (
                <Row width="100%" alignmentHorizontal="center" spacing="200">
                    <Text type="b100" color="secondary">
                        Loading data... ({completionPercentage}%)
                    </Text>
                </Row>
            )}

            <MetricsGrid
                columnDefs={royaltiesGridColDefs}
                rowData={data}
                filterText={filterText}
                onGridReady={handleGridReady}
                defaultSortModel={defaultSortModel}
                isDataFullyLoaded={isDataFullyLoaded}
            />
        </Column>
    );
};
